import { supabase } from '@/lib/supabase';

export interface IntegrationIdentityDetails {
  account_name: string | null;
  account_ref: string | null;
  details: Record<string, unknown>;
  key_hint: string | null;
}

export interface IntegrationCheck {
  status: 'ok' | 'error';
  error_message: string | null;
  verified_at: string;
  /** Present only when the caller is the company owner / co-owner. */
  identity: IntegrationIdentityDetails | null;
}

export interface IntegrationCheckView {
  canViewDetails: boolean;
  check: IntegrationCheck | null;
}

export const integrationIdentityService = {
  async load(companyId: string, integrationId: string): Promise<IntegrationCheckView> {
    const [canViewRes, statusRes] = await Promise.all([
      supabase.rpc('can_view_integration_identity', { p_company_id: companyId }),
      supabase.rpc('get_integration_check_status', { p_company_id: companyId }),
    ]);
    if (canViewRes.error) throw canViewRes.error;
    if (statusRes.error) throw statusRes.error;

    const canViewDetails = canViewRes.data === true;
    const rows = (statusRes.data ?? []) as Array<Omit<IntegrationCheck, 'identity'> & { integration_id: string }>;
    const status = rows.find(r => r.integration_id === integrationId);
    if (!status) return { canViewDetails, check: null };

    let identity: IntegrationIdentityDetails | null = null;
    if (canViewDetails && status.status === 'ok') {
      const { data, error } = await supabase
        .from('integration_identities')
        .select('account_name, account_ref, details, key_hint')
        .eq('integration_id', integrationId)
        .maybeSingle();
      if (error) throw error;
      identity = (data as IntegrationIdentityDetails | null) ?? null;
    }

    return {
      canViewDetails,
      check: { status: status.status, error_message: status.error_message, verified_at: status.verified_at, identity },
    };
  },

  async runCheck(integrationId: string): Promise<IntegrationCheck & { can_view_details: boolean }> {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/integration-identity`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ integration_id: integrationId }),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body || body.error || (body.status !== 'ok' && body.status !== 'error')) {
      throw new Error(body?.error || `הבדיקה נכשלה (${res.status})`);
    }
    return body;
  },
};
